import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Link,
  useLocation,
} from "react-router-dom";

import {
  FaFacebookF,
  FaYoutube,
  FaEnvelope,
  FaMapMarkerAlt,
  FaPhoneAlt,
} from "react-icons/fa";

import {
  HiOutlineMenuAlt3,
} from "react-icons/hi";

import {
  IoClose,
} from "react-icons/io5";

import logo from "./passionaAidsLogo.jpg";


const navLinks = [
  {
    text: "Home",
    path: "/",
  },
  {
    text: "Founder's Message",
    path: "/principalMessagePage",
  },
  {
    text: "Submit Admission",
    path: "/admissionForm",
  },
  {
    text: "Entrance Application",
    path: "/applyForEntrance",
  },
  {
    text: "Entrance Results",
    path: "/entrancePublicResults",
  },
  {
    text: "Scan Attendance",
    path: "/teacherAttendanceScanner",
  },
  {
    text: "Login",
    path: "/login",
    button: true,
  },
];


const Navbar = () => {
  const location = useLocation();

  const [menuOpen, setMenuOpen] =
    useState(false);

  const [scrolled, setScrolled] =
    useState(false);

  const mobileMenuRef =
    useRef(null);

  const mobileToggleRef =
    useRef(null);


  /* =========================================================
     CLOSE MENU WHEN ROUTE CHANGES
  ========================================================= */

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);


  /* =========================================================
     SCROLL STATE
  ========================================================= */

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(
        window.scrollY > 18
      );
    };

    handleScroll();

    window.addEventListener(
      "scroll",
      handleScroll,
      {
        passive: true,
      }
    );

    return () => {
      window.removeEventListener(
        "scroll",
        handleScroll
      );
    };
  }, []);


  /* =========================================================
     LOCK PAGE WHEN MOBILE MENU IS OPEN
  ========================================================= */

  useEffect(() => {
    if (menuOpen) {
      document.body.classList.add(
        "patif-navbar-menu-open"
      );
    } else {
      document.body.classList.remove(
        "patif-navbar-menu-open"
      );
    }

    return () => {
      document.body.classList.remove(
        "patif-navbar-menu-open"
      );
    };
  }, [menuOpen]);


  /* =========================================================
     ESCAPE KEY
  ========================================================= */

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.key === "Escape" &&
        menuOpen
      ) {
        setMenuOpen(false);

        setTimeout(() => {
          mobileToggleRef.current?.focus();
        }, 50);
      }
    };

    document.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      document.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [menuOpen]);


  /* =========================================================
     OUTSIDE CLICK
  ========================================================= */

  useEffect(() => {
    const handleOutsideClick = (
      event
    ) => {
      if (!menuOpen) return;

      const clickedInsideMenu =
        mobileMenuRef.current?.contains(
          event.target
        );

      const clickedToggle =
        mobileToggleRef.current?.contains(
          event.target
        );

      if (
        !clickedInsideMenu &&
        !clickedToggle
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, [menuOpen]);


  /* =========================================================
     ACTIVE ROUTE
  ========================================================= */

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }

    return location.pathname === path;
  };


  /* =========================================================
     MENU CONTROLS
  ========================================================= */

  const toggleMenu = () => {
    setMenuOpen(
      (previous) => !previous
    );
  };


  const closeMenu = () => {
    setMenuOpen(false);
  };


  return (
    <>
      <style>{`

        /* =====================================================
           PASSION AIDS THE FUTURE
           PROFESSIONAL NAVIGATION SYSTEM
        ===================================================== */

        :root {

          --patif-black:
            #070908;

          --patif-black-soft:
            #101513;

          --patif-black-light:
            #18201C;

          --patif-green:
            #168A3F;

          --patif-green-dark:
            #0D642D;

          --patif-green-bright:
            #21A951;

          --patif-lemon:
            #B8F34A;

          --patif-lemon-bright:
            #D4FF61;

          --patif-yellow:
            #F4C430;

          --patif-yellow-bright:
            #FFD84A;

          --patif-white:
            #FFFFFF;

          --patif-off-white:
            #F8FAF8;

          --patif-text:
            #101512;

          --patif-muted:
            #69736D;

          --patif-border:
            #E1E7E3;

          --patif-shadow:
            0 14px 42px
            rgba(
              0,
              0,
              0,
              0.12
            );

          --patif-transition:
            0.28s
            cubic-bezier(
              0.22,
              1,
              0.36,
              1
            );
        }


        /* =====================================================
           RESET
        ===================================================== */

        .patif-site-header,
        .patif-site-header *,
        .patif-mobile-menu,
        .patif-mobile-menu * {
          box-sizing: border-box;
        }


        body.patif-navbar-menu-open {
          overflow: hidden;
        }


        /* =====================================================
           HEADER
        ===================================================== */

        .patif-site-header {

          position: fixed;

          top: 0;
          left: 0;
          right: 0;

          width: 100%;

          z-index: 5000;

          font-family:
            "Montserrat",
            "Poppins",
            "Segoe UI",
            Arial,
            sans-serif;

          background:
            var(--patif-white);
        }


        /* =====================================================
           TOP BAR
        ===================================================== */

        .patif-topbar {

          position: relative;

          width: 100%;

          min-height: 42px;

          color:
            rgba(
              255,
              255,
              255,
              0.90
            );

          background:
            linear-gradient(
              100deg,
              #050706 0%,
              #101714 52%,
              #070908 100%
            );

          border-bottom:
            1px solid
            rgba(
              184,
              243,
              74,
              0.16
            );
        }


        .patif-topbar::after {

          content: "";

          position: absolute;

          left: 0;
          right: 0;
          bottom: 0;

          height: 3px;

          background:
            linear-gradient(
              90deg,
              var(--patif-green),
              var(--patif-lemon),
              var(--patif-yellow),
              var(--patif-green)
            );
        }


        .patif-topbar-inner {

          width:
            min(
              1440px,
              calc(100% - 34px)
            );

          min-height: 42px;

          margin: 0 auto;

          display: flex;

          align-items: center;

          justify-content:
            space-between;

          gap: 20px;
        }


        .patif-topbar-contact {

          display: flex;

          align-items: center;

          gap: 9px;

          min-width: 0;

          color:
            rgba(
              255,
              255,
              255,
              0.75
            );

          font-size: 11px;

          font-weight: 650;

          white-space: nowrap;
        }


        .patif-topbar-contact svg {

          flex-shrink: 0;

          color:
            var(--patif-lemon);

          font-size: 12px;
        }


        .patif-topbar-right {

          display: flex;

          align-items: center;

          gap: 12px;

          flex-shrink: 0;
        }


        .patif-follow {

          color:
            rgba(
              255,
              255,
              255,
              0.48
            );

          font-size: 9px;

          font-weight: 850;

          letter-spacing:
            1.4px;

          text-transform:
            uppercase;
        }


        .patif-top-socials {

          display: flex;

          align-items: center;

          gap: 6px;
        }


        .patif-top-socials a {

          width: 28px;

          height: 28px;

          display: flex;

          align-items: center;

          justify-content: center;

          color:
            rgba(
              255,
              255,
              255,
              0.82
            );

          background:
            rgba(
              255,
              255,
              255,
              0.07
            );

          border:
            1px solid
            rgba(
              255,
              255,
              255,
              0.10
            );

          border-radius: 8px;

          text-decoration: none;

          transition:
            var(--patif-transition);
        }


        .patif-top-socials a:hover {

          color:
            var(--patif-black);

          background:
            var(--patif-lemon);

          border-color:
            var(--patif-lemon);

          transform:
            translateY(-2px);
        }


        /* =====================================================
           MAIN NAVBAR
        ===================================================== */

        .patif-navbar-main {

          width: 100%;

          height: 91px;

          background:
            rgba(
              255,
              255,
              255,
              0.985
            );

          border-bottom:
            1px solid
            var(--patif-border);

          transition:
            height 0.3s ease,
            box-shadow 0.3s ease;
        }


        .patif-site-header-scrolled
        .patif-navbar-main {

          height: 78px;

          background:
            rgba(
              255,
              255,
              255,
              0.99
            );

          box-shadow:
            var(--patif-shadow);
        }


        /* =====================================================
           NAVBAR INNER
        ===================================================== */

        .patif-navbar-inner {

          width:
            min(
              1440px,
              calc(100% - 34px)
            );

          height: 100%;

          margin: 0 auto;

          display: flex;

          align-items: center;

          gap: 22px;
        }


        /* =====================================================
           BRAND
        ===================================================== */

        .patif-brand {

          min-width: 270px;

          flex-shrink: 0;

          display: flex;

          align-items: center;

          gap: 12px;

          color: inherit;

          text-decoration: none;

          transition:
            transform 0.25s ease;
        }


        .patif-brand:hover {

          transform:
            translateY(-1px);
        }


        .patif-logo-wrapper {

          position: relative;

          width: 58px;

          height: 58px;

          flex-shrink: 0;

          padding: 3px;

          display: flex;

          align-items: center;

          justify-content: center;

          background:
            linear-gradient(
              145deg,
              var(--patif-green),
              var(--patif-lemon),
              var(--patif-yellow)
            );

          border-radius: 15px;

          box-shadow:
            0 7px 19px
            rgba(
              22,
              138,
              63,
              0.18
            );

          overflow: hidden;
        }


        .patif-logo-wrapper::before {

          content: "";

          position: absolute;

          width: 12px;

          height: 12px;

          top: 5px;

          right: 5px;

          border-radius: 50%;

          background:
            var(--patif-yellow);

          z-index: 2;
        }


        .patif-navbar-logo {

          position: relative;

          z-index: 1;

          width: 100%;

          height: 100%;

          display: block;

          object-fit: contain;

          background:
            var(--patif-white);

          border-radius: 11px;
        }


        .patif-brand-text {

          min-width: 0;

          display: flex;

          flex-direction: column;

          justify-content: center;
        }


        .patif-brand-name {

          margin: 0;

          color:
            var(--patif-black);

          font-size: 19px;

          font-weight: 950;

          line-height: 1.05;

          letter-spacing:
            -0.65px;

          white-space: nowrap;
        }


        .patif-brand-name span {

          color:
            var(--patif-green);
        }


        .patif-brand-tagline {

          margin: 6px 0 0;

          color:
            var(--patif-muted);

          font-size: 7.5px;

          font-weight: 850;

          line-height: 1;

          letter-spacing:
            1.35px;

          text-transform:
            uppercase;

          white-space: nowrap;
        }


        .patif-brand-tagline::before {

          content: "";

          display: inline-block;

          width: 19px;

          height: 3px;

          margin-right: 6px;

          vertical-align: middle;

          background:
            linear-gradient(
              90deg,
              var(--patif-green),
              var(--patif-lemon)
            );

          border-radius: 10px;
        }


        /* =====================================================
           DESKTOP NAVIGATION
        ===================================================== */

        .patif-desktop-navigation {

          min-width: 0;

          height: 100%;

          margin-left: auto;

          display: flex;

          align-items: center;
        }


        .patif-nav-links {

          height: 100%;

          margin: 0;

          padding: 0;

          display: flex;

          align-items: center;

          justify-content: flex-end;

          gap: 1px;

          list-style: none;
        }


        .patif-nav-links li {

          position: relative;

          height: 100%;

          margin: 0;

          padding: 0;

          display: flex;

          align-items: center;

          list-style: none;
        }


        .patif-nav-links li > a {

          position: relative;

          min-height: 44px;

          padding:
            0 11px;

          display: inline-flex;

          align-items: center;

          justify-content: center;

          color:
            var(--patif-text);

          background:
            transparent;

          border-radius: 9px;

          font-size: 13px;

          font-weight: 750;

          line-height: 1;

          letter-spacing:
            -0.15px;

          text-decoration: none;

          white-space: nowrap;

          transition:
            color 0.22s ease,
            background 0.22s ease,
            transform 0.22s ease;
        }


        /* =====================================================
           NAV HOVER
        ===================================================== */

        .patif-nav-links
        li:not(.patif-login-item)
        > a::before {

          content: "";

          position: absolute;

          left: 5px;

          right: 5px;

          top: 5px;

          bottom: 5px;

          border-radius: 7px;

          background:
            rgba(
              22,
              138,
              63,
              0.065
            );

          opacity: 0;

          transform:
            scale(0.94);

          transition:
            opacity 0.22s ease,
            transform 0.22s ease;
        }


        .patif-nav-links
        li:not(.patif-login-item)
        > a:hover::before {

          opacity: 1;

          transform:
            scale(1);
        }


        .patif-nav-links
        li:not(.patif-login-item)
        > a:hover {

          color:
            var(--patif-green-dark);

          transform:
            translateY(-1px);
        }


        /* =====================================================
           ACTIVE LINK
        ===================================================== */

        .patif-nav-links
        li:not(.patif-login-item).active
        > a {

          color:
            var(--patif-green-dark);

          font-weight: 900;
        }


        .patif-nav-links
        li:not(.patif-login-item).active
        > a::after {

          content: "";

          position: absolute;

          left: 50%;

          bottom: 3px;

          width: 25px;

          height: 3px;

          border-radius: 20px;

          background:
            linear-gradient(
              90deg,
              var(--patif-green),
              var(--patif-lemon)
            );

          transform:
            translateX(-50%);
        }


        /* =====================================================
           LOGIN BUTTON
        ===================================================== */

        .patif-login-item {

          margin-left: 7px !important;

          padding-left: 9px !important;

          border-left:
            1px solid
            var(--patif-border);
        }


        .patif-nav-links
        .patif-login-button {

          min-width: 102px;

          height: 43px;

          padding:
            0 17px;

          overflow: hidden;

          color:
            var(--patif-black) !important;

          background:
            linear-gradient(
              135deg,
              var(--patif-lemon),
              var(--patif-yellow)
            );

          border:
            1px solid
            rgba(
              22,
              138,
              63,
              0.20
            );

          border-radius: 10px;

          font-size: 13px;

          font-weight: 900 !important;

          box-shadow:
            0 6px 16px
            rgba(
              22,
              138,
              63,
              0.13
            );

          transition:
            transform 0.25s ease,
            box-shadow 0.25s ease,
            background 0.25s ease;
        }


        .patif-login-button::before {

          content: "";

          position: absolute;

          top: 0;

          left: -120%;

          width: 70%;

          height: 100%;

          background:
            linear-gradient(
              90deg,
              transparent,
              rgba(
                255,
                255,
                255,
                0.70
              ),
              transparent
            );

          transform:
            skewX(-20deg);

          transition:
            left 0.55s ease;
        }


        .patif-login-button:hover {

          color:
            var(--patif-black) !important;

          background:
            linear-gradient(
              135deg,
              var(--patif-lemon-bright),
              var(--patif-yellow-bright)
            );

          transform:
            translateY(-2px);

          box-shadow:
            0 10px 22px
            rgba(
              22,
              138,
              63,
              0.19
            );
        }


        .patif-login-button:hover::before {

          left: 140%;
        }


        /* =====================================================
           MOBILE TOGGLE
        ===================================================== */

        .patif-mobile-toggle {

          display: none;

          width: 47px;

          height: 47px;

          margin-left: auto;

          align-items: center;

          justify-content: center;

          color:
            var(--patif-white);

          background:
            var(--patif-black);

          border:
            2px solid
            var(--patif-green);

          border-radius: 11px;

          font-size: 26px;

          cursor: pointer;

          transition:
            var(--patif-transition);
        }


        .patif-mobile-toggle:hover,
        .patif-mobile-toggle.active {

          color:
            var(--patif-black);

          background:
            var(--patif-lemon);

          border-color:
            var(--patif-lemon);
        }


        /* =====================================================
           BACKDROP
        ===================================================== */

        .patif-mobile-backdrop {

          position: fixed;

          inset: 0;

          z-index: 4998;

          background:
            rgba(
              7,
              9,
              8,
              0.68
            );

          backdrop-filter:
            blur(5px);

          -webkit-backdrop-filter:
            blur(5px);

          opacity: 0;

          visibility: hidden;

          transition:
            opacity 0.3s ease,
            visibility 0.3s ease;
        }


        .patif-mobile-backdrop.show {

          opacity: 1;

          visibility: visible;
        }


        /* =====================================================
           MOBILE DRAWER
        ===================================================== */

        .patif-mobile-menu {

          position: fixed;

          top: 0;

          right: 0;

          width:
            min(
              390px,
              93vw
            );

          height: 100vh;

          z-index: 5001;

          display: flex;

          flex-direction: column;

          overflow-y: auto;

          overflow-x: hidden;

          background:
            var(--patif-white);

          border-left:
            5px solid
            var(--patif-green);

          box-shadow:
            -20px 0 55px
            rgba(
              0,
              0,
              0,
              0.28
            );

          transform:
            translateX(105%);

          visibility: hidden;

          transition:
            transform 0.34s
            cubic-bezier(
              0.22,
              1,
              0.36,
              1
            ),
            visibility 0.34s ease;
        }


        .patif-mobile-menu.show {

          transform:
            translateX(0);

          visibility: visible;
        }


        /* =====================================================
           MOBILE HEADER
        ===================================================== */

        .patif-mobile-header {

          position: relative;

          min-height: 108px;

          padding:
            18px;

          display: flex;

          align-items: center;

          justify-content: space-between;

          background:
            linear-gradient(
              135deg,
              var(--patif-black),
              var(--patif-black-light)
            );

          overflow: hidden;

          border-bottom:
            4px solid
            var(--patif-lemon);
        }


        .patif-mobile-header::after {

          content: "";

          position: absolute;

          width: 150px;

          height: 150px;

          right: -70px;

          top: -65px;

          border-radius: 50%;

          background:
            rgba(
              184,
              243,
              74,
              0.10
            );
        }


        .patif-mobile-brand {

          position: relative;

          z-index: 2;

          display: flex;

          align-items: center;

          gap: 12px;

          min-width: 0;
        }


        .patif-mobile-brand img {

          width: 53px;

          height: 53px;

          flex-shrink: 0;

          object-fit: contain;

          padding: 2px;

          background:
            var(--patif-white);

          border:
            2px solid
            var(--patif-lemon);

          border-radius: 13px;
        }


        .patif-mobile-brand div {

          min-width: 0;

          display: flex;

          flex-direction: column;
        }


        .patif-mobile-brand strong {

          color:
            var(--patif-white);

          font-size: 16px;

          font-weight: 950;

          line-height: 1.15;
        }


        .patif-mobile-brand strong span {

          color:
            var(--patif-lemon);
        }


        .patif-mobile-brand small {

          margin-top: 5px;

          color:
            rgba(
              255,
              255,
              255,
              0.58
            );

          font-size: 8px;

          font-weight: 800;

          letter-spacing:
            1.1px;

          text-transform:
            uppercase;
        }


        .patif-mobile-close {

          position: relative;

          z-index: 3;

          width: 42px;

          height: 42px;

          flex-shrink: 0;

          display: flex;

          align-items: center;

          justify-content: center;

          color:
            var(--patif-white);

          background:
            rgba(
              255,
              255,
              255,
              0.08
            );

          border:
            1px solid
            rgba(
              255,
              255,
              255,
              0.15
            );

          border-radius: 11px;

          font-size: 24px;

          cursor: pointer;

          transition:
            var(--patif-transition);
        }


        .patif-mobile-close:hover {

          color:
            var(--patif-black);

          background:
            var(--patif-lemon);

          border-color:
            var(--patif-lemon);
        }


        /* =====================================================
           MOBILE NAVIGATION
        ===================================================== */

        .patif-mobile-navigation {

          padding:
            22px 16px 18px;
        }


        .patif-mobile-navigation ul {

          width: 100%;

          margin: 0;

          padding: 0;

          display: flex;

          flex-direction: column;

          gap: 8px;

          list-style: none;
        }


        .patif-mobile-navigation li {

          width: 100%;

          margin: 0;

          padding: 0;

          list-style: none;
        }


        .patif-mobile-navigation li a {

          position: relative;

          width: 100%;

          min-height: 58px;

          padding:
            0 17px;

          display: flex;

          align-items: center;

          justify-content: space-between;

          color:
            var(--patif-text);

          background:
            var(--patif-off-white);

          border:
            1px solid
            var(--patif-border);

          border-radius: 12px;

          font-size: 16px;

          font-weight: 750;

          text-decoration: none;

          transition:
            var(--patif-transition);
        }


        .patif-mobile-navigation
        li a::before {

          content: "";

          position: absolute;

          left: 0;

          top: 50%;

          width: 4px;

          height: 0;

          background:
            linear-gradient(
              var(--patif-green),
              var(--patif-lemon)
            );

          border-radius:
            0 5px 5px 0;

          transform:
            translateY(-50%);

          transition:
            height 0.22s ease;
        }


        .patif-mobile-navigation
        li a:hover {

          color:
            var(--patif-green-dark);

          background:
            rgba(
              184,
              243,
              74,
              0.13
            );

          border-color:
            rgba(
              22,
              138,
              63,
              0.25
            );

          transform:
            translateX(3px);
        }


        .patif-mobile-navigation
        li a:hover::before {

          height: 65%;
        }


        .patif-mobile-navigation
        li.active
        > a:not(
          .patif-mobile-login
        ) {

          color:
            var(--patif-green-dark);

          background:
            linear-gradient(
              135deg,
              rgba(
                184,
                243,
                74,
                0.20
              ),
              rgba(
                244,
                196,
                48,
                0.08
              )
            );

          border-color:
            var(--patif-green);

          font-weight: 900;
        }


        .patif-mobile-navigation
        li.active
        > a:not(
          .patif-mobile-login
        )::before {

          height: 72%;
        }


        .patif-mobile-arrow {

          color:
            var(--patif-green);

          font-size: 18px;

          font-weight: 900;

          transition:
            transform 0.2s ease;
        }


        .patif-mobile-navigation
        li a:hover
        .patif-mobile-arrow {

          transform:
            translateX(4px);

          color:
            var(--patif-green-dark);
        }


        /* =====================================================
           MOBILE LOGIN
        ===================================================== */

        .patif-mobile-login {

          justify-content:
            center !important;

          margin-top: 5px;

          color:
            var(--patif-black) !important;

          background:
            linear-gradient(
              135deg,
              var(--patif-lemon),
              var(--patif-yellow)
            ) !important;

          border:
            1px solid
            var(--patif-green) !important;

          font-weight:
            900 !important;

          box-shadow:
            0 8px 20px
            rgba(
              22,
              138,
              63,
              0.15
            );
        }


        .patif-mobile-login:hover {

          color:
            var(--patif-black)
            !important;

          background:
            linear-gradient(
              135deg,
              var(--patif-lemon-bright),
              var(--patif-yellow-bright)
            ) !important;

          transform:
            translateY(-2px)
            !important;
        }


        /* =====================================================
           MOBILE CONTACT
        ===================================================== */

        .patif-mobile-contact {

          margin-top: auto;

          padding:
            20px;

          display: flex;

          flex-direction: column;

          gap: 13px;

          background:
            var(--patif-off-white);

          border-top:
            1px solid
            var(--patif-border);
        }


        .patif-mobile-contact-item {

          display: flex;

          align-items: flex-start;

          gap: 11px;

          color:
            var(--patif-muted);

          font-size: 11px;

          line-height: 1.55;
        }


        .patif-mobile-contact-item svg {

          flex-shrink: 0;

          margin-top: 2px;

          color:
            var(--patif-green);
        }


        /* =====================================================
           MOBILE SOCIAL
        ===================================================== */

        .patif-mobile-social {

          padding:
            17px 20px;

          display: flex;

          align-items: center;

          justify-content:
            space-between;

          gap: 15px;

          background:
            var(--patif-white);
        }


        .patif-mobile-social > span {

          color:
            var(--patif-muted);

          font-size: 9px;

          font-weight: 850;

          letter-spacing:
            1.2px;

          text-transform:
            uppercase;
        }


        .patif-mobile-social-links {

          display: flex;

          align-items: center;

          gap: 7px;
        }


        .patif-mobile-social-links a {

          width: 35px;

          height: 35px;

          display: flex;

          align-items: center;

          justify-content: center;

          color:
            var(--patif-green-dark);

          background:
            rgba(
              22,
              138,
              63,
              0.07
            );

          border:
            1px solid
            rgba(
              22,
              138,
              63,
              0.14
            );

          border-radius: 9px;

          text-decoration: none;

          transition:
            var(--patif-transition);
        }


        .patif-mobile-social-links
        a:hover {

          color:
            var(--patif-black);

          background:
            var(--patif-lemon);

          border-color:
            var(--patif-lemon);

          transform:
            translateY(-2px);
        }


        /* =====================================================
           MOBILE FOOTER
        ===================================================== */

        .patif-mobile-footer {

          padding:
            17px 20px 24px;

          text-align: center;

          background:
            var(--patif-black);
        }


        .patif-mobile-footer strong {

          color:
            var(--patif-white);

          font-size: 13px;

          font-weight: 950;
        }


        .patif-mobile-footer strong span {

          color:
            var(--patif-lemon);
        }


        .patif-mobile-footer small {

          display: block;

          margin-top: 6px;

          color:
            rgba(
              255,
              255,
              255,
              0.48
            );

          font-size: 8px;

          font-weight: 750;

          letter-spacing:
            1.3px;

          text-transform:
            uppercase;
        }


        /* =====================================================
           NAVBAR SPACING
        ===================================================== */

        .patif-navbar-spacing {

          width: 100%;

          height: 133px;
        }


        /* =====================================================
           LARGE LAPTOP
        ===================================================== */

        @media (max-width: 1350px) {

          .patif-navbar-inner {

            width:
              calc(100% - 28px);

            gap: 14px;
          }


          .patif-brand {

            min-width: 250px;

            gap: 10px;
          }


          .patif-logo-wrapper {

            width: 54px;

            height: 54px;
          }


          .patif-brand-name {

            font-size: 17px;

            letter-spacing:
              -0.5px;
          }


          .patif-brand-tagline {

            font-size: 7px;

            letter-spacing:
              1.1px;
          }


          .patif-nav-links {

            gap: 0;
          }


          .patif-nav-links li > a {

            padding:
              0 8px;

            font-size: 12px;
          }


          .patif-login-item {

            margin-left: 5px !important;

            padding-left: 7px !important;
          }


          .patif-nav-links
          .patif-login-button {

            min-width: 94px;

            padding:
              0 14px;
          }
        }


        /* =====================================================
           COMPACT LAPTOP
        ===================================================== */

        @media (max-width: 1180px) {

          .patif-navbar-inner {

            width:
              calc(100% - 24px);

            gap: 10px;
          }


          .patif-brand {

            min-width: 225px;
          }


          .patif-logo-wrapper {

            width: 50px;

            height: 50px;
          }


          .patif-brand-name {

            font-size: 15px;
          }


          .patif-brand-tagline {

            font-size: 6.5px;

            letter-spacing:
              0.9px;
          }


          .patif-nav-links li > a {

            padding:
              0 6px;

            font-size: 11.5px;
          }


          .patif-nav-links
          .patif-login-button {

            min-width: 88px;

            height: 41px;

            padding:
              0 12px;

            font-size: 12px;
          }
        }


        /* =====================================================
           TABLET
        ===================================================== */

        @media (max-width: 980px) {

          .patif-topbar {

            display: none;
          }


          .patif-navbar-main {

            height: 78px;
          }


          .patif-site-header-scrolled
          .patif-navbar-main {

            height: 72px;
          }


          .patif-navbar-inner {

            width:
              calc(100% - 30px);

            gap: 12px;
          }


          .patif-desktop-navigation {

            display: none;
          }


          .patif-mobile-toggle {

            display: flex;
          }


          .patif-logo-wrapper {

            width: 54px;

            height: 54px;
          }


          .patif-brand {

            min-width: 0;

            flex: 1;
          }


          .patif-brand-name {

            font-size: 18px;
          }


          .patif-brand-tagline {

            font-size: 7px;

            letter-spacing:
              1px;
          }


          .patif-navbar-spacing {

            height: 78px;
          }
        }


        /* =====================================================
           MOBILE
        ===================================================== */

        @media (max-width: 600px) {

          .patif-navbar-main {

            height: 70px;
          }


          .patif-site-header-scrolled
          .patif-navbar-main {

            height: 66px;
          }


          .patif-navbar-inner {

            width:
              calc(100% - 20px);

            gap: 10px;
          }


          .patif-brand {

            gap: 9px;
          }


          .patif-logo-wrapper {

            width: 46px;

            height: 46px;

            padding: 2px;

            border-radius: 12px;
          }


          .patif-logo-wrapper::before {

            width: 9px;

            height: 9px;

            top: 3px;

            right: 3px;
          }


          .patif-brand-name {

            font-size: 14px;

            letter-spacing:
              -0.3px;
          }


          .patif-brand-tagline {

            margin-top: 4px;

            font-size: 6px;

            letter-spacing:
              0.9px;
          }


          .patif-brand-tagline::before {

            width: 13px;

            height: 2px;

            margin-right: 4px;
          }


          .patif-mobile-toggle {

            width: 43px;

            height: 43px;

            font-size: 24px;
          }


          .patif-navbar-spacing {

            height: 70px;
          }
        }


        /* =====================================================
           SMALL PHONE
        ===================================================== */

        @media (max-width: 390px) {

          .patif-navbar-inner {

            width:
              calc(100% - 16px);
          }


          .patif-logo-wrapper {

            width: 42px;

            height: 42px;
          }


          .patif-brand-name {

            font-size: 12px;
          }


          .patif-brand-tagline {

            font-size: 5.5px;

            letter-spacing:
              0.7px;
          }


          .patif-mobile-toggle {

            width: 40px;

            height: 40px;
          }
        }


        /* =====================================================
           EXTRA SMALL
        ===================================================== */

        @media (max-width: 320px) {

          .patif-brand-name {

            font-size: 11px;
          }


          .patif-brand-tagline {

            display: none;
          }


          .patif-logo-wrapper {

            width: 39px;

            height: 39px;
          }


          .patif-mobile-toggle {

            width: 38px;

            height: 38px;
          }
        }


        /* =====================================================
           ACCESSIBILITY
        ===================================================== */

        .patif-nav-links a:focus-visible,
        .patif-mobile-navigation a:focus-visible,
        .patif-mobile-toggle:focus-visible,
        .patif-mobile-close:focus-visible,
        .patif-top-socials a:focus-visible,
        .patif-mobile-social-links a:focus-visible {

          outline:
            3px solid
            var(--patif-yellow);

          outline-offset:
            3px;
        }


        /* =====================================================
           REDUCED MOTION
        ===================================================== */

        @media (
          prefers-reduced-motion: reduce
        ) {

          .patif-site-header *,
          .patif-mobile-menu * {

            transition:
              none !important;
          }
        }

      `}</style>


      {/* =======================================================
          HEADER
      ======================================================= */}

      <header
        className={`
          patif-site-header
          ${
            scrolled
              ? "patif-site-header-scrolled"
              : ""
          }
        `}
      >


        {/* =====================================================
            TOP INFORMATION BAR
        ===================================================== */}

        <div className="patif-topbar">

          <div className="patif-topbar-inner">

            <div className="patif-topbar-contact">

              <FaMapMarkerAlt />

              <span>
                Paynesville Monrovia, Liberia
              </span>

              <span>
                •
              </span>

              <span>
                {/* +231-776-4940-10 */}
              </span>

              <span>
                {/* / */}
              </span>

              <span>
                {/* +231-880-5858-55 */}
              </span>

            </div>


            <div className="patif-topbar-right">

              <span className="patif-follow">
                Follow PATIF
              </span>


              <div className="patif-top-socials">

                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                >
                  <FaFacebookF />
                </a>


                <a
                  href="https://youtube.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="YouTube"
                >
                  <FaYoutube />
                </a>


                <a
                  href="mailto:info@passionaids.org"
                  aria-label="Email"
                >
                  <FaEnvelope />
                </a>

              </div>

            </div>

          </div>

        </div>


        {/* =====================================================
            MAIN NAVIGATION
        ===================================================== */}

        <div className="patif-navbar-main">

          <div className="patif-navbar-inner">


            {/* =================================================
                BRAND
            ================================================= */}

            <Link
              to="/"
              className="patif-brand"
              aria-label="PASSION AIDS THE FUTURE Home"
            >

              <div className="patif-logo-wrapper">

                <img
                  src={logo}
                  alt="PASSION AIDS THE FUTURE"
                  className="patif-navbar-logo"
                />

              </div>


              <div className="patif-brand-text">

                <h1 className="patif-brand-name">

                  PASSION{" "}

                  <span>
                    AIDS THE FUTURE
                  </span>

                </h1>


                <p className="patif-brand-tagline">
                  Professional • Vocational • Leadership
                </p>

              </div>

            </Link>


            {/* =================================================
                DESKTOP NAVIGATION
            ================================================= */}

            <nav
              className="patif-desktop-navigation"
              aria-label="Main navigation"
            >

              <ul className="patif-nav-links">

                {navLinks.map((link) => (

                  <li
                    key={link.path}
                    className={`
                      ${
                        isActive(link.path)
                          ? "active"
                          : ""
                      }
                      ${
                        link.button
                          ? "patif-login-item"
                          : ""
                      }
                    `}
                  >

                    <Link
                      to={link.path}
                      className={
                        link.button
                          ? "patif-login-button"
                          : ""
                      }
                    >

                      {link.text}

                    </Link>

                  </li>

                ))}

              </ul>

            </nav>


            {/* =================================================
                MOBILE TOGGLE
            ================================================= */}

            <button
              ref={mobileToggleRef}
              type="button"
              className={`
                patif-mobile-toggle
                ${
                  menuOpen
                    ? "active"
                    : ""
                }
              `}
              onClick={toggleMenu}
              aria-label={
                menuOpen
                  ? "Close navigation menu"
                  : "Open navigation menu"
              }
              aria-expanded={menuOpen}
              aria-controls="patif-mobile-navigation"
            >

              {menuOpen ? (
                <IoClose />
              ) : (
                <HiOutlineMenuAlt3 />
              )}

            </button>

          </div>

        </div>


        {/* =====================================================
            MOBILE BACKDROP
        ===================================================== */}

        <div
          className={`
            patif-mobile-backdrop
            ${
              menuOpen
                ? "show"
                : ""
            }
          `}
          onClick={closeMenu}
          aria-hidden="true"
        />


        {/* =====================================================
            MOBILE DRAWER
        ===================================================== */}

        <aside
          ref={mobileMenuRef}
          id="patif-mobile-navigation"
          className={`
            patif-mobile-menu
            ${
              menuOpen
                ? "show"
                : ""
            }
          `}
          aria-hidden={!menuOpen}
        >


          {/* =================================================
              MOBILE BRAND HEADER
          ================================================= */}

          <div className="patif-mobile-header">

            <div className="patif-mobile-brand">

              <img
                src={logo}
                alt="PASSION AIDS THE FUTURE"
              />

              <div>

                <strong>

                  PASSION{" "}

                  <span>
                    AIDS THE FUTURE
                  </span>

                </strong>

                <small>
                  Professional & Vocational Education
                </small>

              </div>

            </div>


            <button
              type="button"
              className="patif-mobile-close"
              onClick={closeMenu}
              aria-label="Close navigation menu"
            >

              <IoClose />

            </button>

          </div>


          {/* =================================================
              MOBILE NAVIGATION
          ================================================= */}

          <nav
            className="patif-mobile-navigation"
            aria-label="Mobile navigation"
          >

            <ul>

              {navLinks.map((link) => (

                <li
                  key={link.path}
                  className={
                    isActive(link.path)
                      ? "active"
                      : ""
                  }
                >

                  <Link
                    to={link.path}
                    onClick={closeMenu}
                    tabIndex={
                      menuOpen
                        ? 0
                        : -1
                    }
                    className={
                      link.button
                        ? "patif-mobile-login"
                        : ""
                    }
                  >

                    <span>
                      {link.text}
                    </span>

                    {!link.button && (
                      <span className="patif-mobile-arrow">
                        →
                      </span>
                    )}

                  </Link>

                </li>

              ))}

            </ul>

          </nav>


          {/* =================================================
              CONTACT
          ================================================= */}

          <div className="patif-mobile-contact">

            <div className="patif-mobile-contact-item">

              <FaMapMarkerAlt />

              <span>
                Johnsonville, Monrovia, Liberia
              </span>

            </div>


            <div className="patif-mobile-contact-item">

              <FaPhoneAlt />

              <span>
                +231-776-4940-10
                <br />
                +231-880-5858-55
              </span>

            </div>


            <div className="patif-mobile-contact-item">

              <FaEnvelope />

              <span>
                info@passionaids.org
              </span>

            </div>

          </div>


          {/* =================================================
              SOCIALS
          ================================================= */}

          <div className="patif-mobile-social">

            <span>
              Connect With PATIF
            </span>


            <div className="patif-mobile-social-links">

              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <FaFacebookF />
              </a>


              <a
                href="https://youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
              >
                <FaYoutube />
              </a>


              <a
                href="mailto:info@passionaids.org"
                aria-label="Email"
              >
                <FaEnvelope />
              </a>

            </div>

          </div>


          {/* =================================================
              MOBILE FOOTER
          ================================================= */}

          <div className="patif-mobile-footer">

            <strong>

              PASSION{" "}

              <span>
                AIDS THE FUTURE
              </span>

            </strong>

            <small>
              Passion • Education • Skills • Leadership
            </small>

          </div>

        </aside>

      </header>


      {/* =======================================================
          PAGE OFFSET
      ======================================================= */}

      <div className="patif-navbar-spacing" />

    </>
  );
};


export default Navbar;